/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { UserManager } from "oidc-client";
import { appInsights } from "services/telemetry-service/telemetry";
import { removeCredentials, setSilentSigninError, userAuthenticated } from "store/features";
import { store } from "store/reduxStore";
import { IDENTITY_CONFIG } from "utils/Oidc";

export const userManager = new UserManager(IDENTITY_CONFIG);

export interface SignInState {
  redirect: string;
}

let processingSilentSignIn: Promise<boolean> | undefined;

userManager.events.addUserLoaded((user) => {
  const authState = store.getState().auth;
  if (authState.accessToken !== user.access_token || authState.expires !== user.expires_at) {
    store.dispatch(
      userAuthenticated({
        user: {
          id: user.profile.sub,
          firstName: user.profile.given_name,
          lastName: user.profile.family_name,
          name: user.profile.name,
          isBentleyUser: user.profile.name?.endsWith("bentley.com"),
        },
        accessToken: user.access_token,
        expires: user.expires_at,
      }));

    appInsights.setAuthenticatedUserContext(user.profile.sub, user.profile.name);
  }
});

userManager.events.addAccessTokenExpiring(() => {
  userManager.signinSilent()
    .then(() => {
      store.dispatch(setSilentSigninError(false));
      console.debug("silent sign-in successful");
    })
    .catch((error) => {
      store.dispatch(removeCredentials());
      store.dispatch(setSilentSigninError(true));
      console.warn("silent sign-in error ", error);
    });
});

export class AuthManager {

  public static async signIn(state?: SignInState) {
    const user = await userManager.getUser();

    if (!user || user.expired) {
      if (store.getState().auth.silentSigninError) {
        void userManager.signinPopup({ state });
      } else {
        const waiter = processingSilentSignIn ?? AuthManager.silentsignIn();

        waiter.then((result) => {
          if (!result)
            void userManager.signinPopup({ state });
        });
      }
    } else {
      userManager.events.load(user);
    }
  }

  public static async silentsignIn() {
    if (processingSilentSignIn) {
      return await processingSilentSignIn;
    }

    processingSilentSignIn = new Promise(async (resolve) => {
      const user = await userManager.getUser();

      if (user && !user.expired) {
        userManager.events.load(user);
        processingSilentSignIn = undefined;
        return resolve(true);
      }

      try {
        await userManager.signinSilent();
        store.dispatch(setSilentSigninError(false));
        console.debug("silent sign-in successful");
        processingSilentSignIn = undefined;
        return resolve(true);
      } catch (error) {
        store.dispatch(setSilentSigninError(true));
        console.warn(`silent sign-in failed. ${error}`);
        processingSilentSignIn = undefined;
        return resolve(false);
      }
    });

    return await processingSilentSignIn;
  }

  public static async signOut() {
    void userManager.signoutRedirect();
    appInsights.clearAuthenticatedUserContext();
  }

  public static openUserProfile() {
    window.open("https://usermanagement.bentley.com/profile", "_blank")
  }
}
